'use server'

import pool from '@/lib/db';
import { signJWT } from '@/lib/jwt';
import { hashPassword, verifyPassword } from '@/lib/password';
import { cookies } from 'next/headers';
import { redirect } from 'next/navigation';

export async function signup(prevState: any, formData: FormData) {
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const phone = formData.get('phone') as string;
    const timezone = formData.get('timezone') as string || 'UTC';

    if (!email || !password) {
        return { message: 'Email and Password are required' };
    }

    try {
        const hashedPassword = await hashPassword(password);

        // Check if first user (make Super Admin)
        const [rows]: any = await pool.query('SELECT COUNT(*) as count FROM users');
        const isFirstUser = rows[0].count === 0;
        const role = isFirstUser ? 'super_admin' : 'user';

        const [insertUserResult]: any = await pool.query(
            'INSERT INTO users (email, phone, password, role, timezone, last_login_at) VALUES (?, ?, ?, ?, ?, NOW())',
            [email, phone, hashedPassword, role, timezone]
        );
        const newUserId = insertUserResult.insertId;

        // Auto-login
        const token = await signJWT({ id: newUserId, email, role });

        // Store Session in DB
        const expiresAt = new Date(Date.now() + 2 * 60 * 60 * 1000); // 2 hours
        // We decode the token or just use it as the key. Since JWT is self-contained, we can store it.
        // But for revocation, we just need to map user_id to a 'valid_until' or store the specific token.
        // Let's store the exact token.
        await pool.query('INSERT INTO sessions (user_id, token, expires_at) VALUES (?, ?, ?)',
            [newUserId, token, expiresAt]
        );

        // Set HTTP-only Cookie
        (await cookies()).set('token', token, {
            httpOnly: true,
            secure: process.env.NODE_ENV === 'production',
            maxAge: 7200, // 2 hours
            path: '/'
        });

        return { success: true };
    } catch (e: any) {
        console.error(e)
        if (e.code === 'ER_DUP_ENTRY') {
            return { message: 'Email already exists' };
        }
        return { message: 'Registration failed' };
    }
}

export async function login(prevState: any, formData: FormData) {
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
        const [rows]: any = await pool.query('SELECT * FROM users WHERE email = ?', [email]);
        const user = rows[0];

        if (!user || !(await verifyPassword(password, user.password))) {
            return { message: 'Invalid credentials' };
        }

        // Update Last Login
        await pool.query('UPDATE users SET last_login_at = NOW() WHERE id = ?', [user.id]);

        const token = await signJWT({ id: user.id, email: user.email, role: user.role, timezone: user.timezone });
        const expiresAt = new Date(Date.now() + 2 * 60 * 60 * 1000); // 2 hours

        await pool.query('INSERT INTO sessions (user_id, token, expires_at) VALUES (?, ?, ?)',
            [user.id, token, expiresAt]
        );

        (await cookies()).set('token', token, {
            httpOnly: true,
            secure: process.env.NODE_ENV === 'production',
            maxAge: 7200,
            path: '/'
        });

        return { success: true };
    } catch (e) {
        return { message: 'Login failed' };
    }
}

export async function logout() {
    const cookieStore = await cookies();
    const token = cookieStore.get('token')?.value;

    if (token) {
        try {
            await pool.query('DELETE FROM sessions WHERE token = ?', [token]);
        } catch (e) { }
    }

    cookieStore.delete('token');
    redirect('/');
}
